import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useOperatorMode } from '../../lib/operatorMode.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  Breadcrumbs,
  EmptyState,
  ErrorState,
  GaugeCard,
  HealthIndicator,
  InlineScoreCell,
  KpiHeroBand,
  LoadingSkeleton,
  bandTone,
  deriveKpiTone,
  toast,
} from '../../components/index.js';
import './rapid.redesign.css';
import { RapidEvidenceDrawer } from './RapidLeadershipUi.jsx';
import {
  RapidStateStrip,
  GuidedTourPanel,
  ResetDialog,
  SeededBanner,
} from './RapidSharedChrome.jsx';
import {
  ApprovalTab,
  ActionsTab,
  CriteriaTab,
  IntakeTab,
  OverviewTab,
  PodsTab,
  RoutedCandidateView,
} from './RapidDetailTabsPrimary.jsx';
import {
  CanvasTab,
  CloseoutTab,
  DiagnosisTab,
  GovernanceTab,
  MetricsTab,
  QaTab,
  ShellsTab,
} from './RapidDetailTabsWorkflow.jsx';
import {
  clearRapidBrowserState,
  persistDetailState,
  persistGlobalState,
  readDetailState,
  readGlobalState,
  readTourState,
  toReviewStatePayload,
  writeTourState,
} from './rapidPageState.js';
import {
  DETAIL_PHASES,
  DETAIL_TAB_LABELS,
  buildDetailSearch,
  detailTabFromSearch,
  formatCriteriaRecommendation,
  formatMoney,
  nowTimestamp,
  tabForTourStep,
  tourRouteForStep,
} from './rapidPageUtils.js';

// AC-RAPID-4: how long edits batch locally before the review-state decision
// batch is persisted server-side with reviewer attribution.
const REVIEW_SYNC_DEBOUNCE_MS = 400;

export function RapidDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const detailRes = useApi(`/rapids/${id}`);
  const resetMutation = useMutation();
  const briefMutation = useMutation();
  const validateMutation = useMutation();
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [globalState, setGlobalState] = useState(null);
  const [detailState, setDetailState] = useState(null);
  const [tourState, setTourState] = useState(readTourState());
  const [generatedBrief, setGeneratedBrief] = useState(null);
  const [evidenceDrawer, setEvidenceDrawer] = useState(null);
  const tabsRef = useRef(null);
  // AC-RAPID-2: Reset workspace, guided tour, and role-lens scripts are demo
  // chrome and render only inside operator mode (AC-SHELL-4).
  const { enabled: operatorModeEnabled } = useOperatorMode();

  const detail = detailRes.data?.data;
  const rapid = detail?.rapid;
  const tab = detailTabFromSearch(searchParams);

  usePageMetadata({
    title: rapid?.title || 'RAPID',
    subtitle: rapid?.summary || undefined,
  });

  useEffect(() => {
    if (!rapid?.seedVersion) return;
    setGlobalState(readGlobalState(rapid.seedVersion));
    // AC-RAPID-4: the server-persisted review state seeds the local cache so a
    // different browser/user sees the same accept/edit/reject decisions.
    setDetailState(readDetailState(rapid, rapid.reviewState || null));
  }, [rapid]);

  // Debounced server sync of the human-review decision batch (AC-RAPID-4).
  const reviewSyncTimer = useRef(null);
  const reviewSyncFailed = useRef(false);
  useEffect(() => () => cancelReviewSync(), []);

  function cancelReviewSync() {
    if (reviewSyncTimer.current) {
      clearTimeout(reviewSyncTimer.current);
      reviewSyncTimer.current = null;
    }
  }

  function scheduleReviewSync(rapidId, persisted) {
    cancelReviewSync();
    reviewSyncTimer.current = setTimeout(async () => {
      reviewSyncTimer.current = null;
      try {
        await api.post(`/rapids/${rapidId}/review-state`, {
          reviewState: toReviewStatePayload(persisted),
        });
        reviewSyncFailed.current = false;
      } catch (error) {
        if (!reviewSyncFailed.current) {
          toast.error(
            error.message ||
              'Review decisions could not be saved to the server. They remain cached in this browser.',
          );
        }
        reviewSyncFailed.current = true;
      }
    }, REVIEW_SYNC_DEBOUNCE_MS);
  }

  useEffect(() => {
    if (!rapid?.id || !tourState.open) return;
    // BL-16: the guided tour is operator chrome. A stale open-tour flag left in
    // storage must never hijack a normal user's navigation — it made RAPID
    // titles appear to "not navigate" (instant bounce back to the list).
    if (!operatorModeEnabled) {
      const cleared = { ...tourState, open: false };
      writeTourState(cleared);
      setTourState(cleared);
      return;
    }
    if (tourState.step <= 2) {
      navigate('/rapids');
      return;
    }
    const expectedTab = tabForTourStep(tourState.step);
    if (expectedTab && tab !== expectedTab) {
      setSearchParams(buildDetailSearch(searchParams, expectedTab), { replace: true });
    }
  }, [
    rapid?.id,
    tourState,
    operatorModeEnabled,
    tab,
    navigate,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el || typeof document === 'undefined') return undefined;
    const apply = () =>
      document.documentElement.style.setProperty(
        '--rapid-tabbar-height',
        `${el.getBoundingClientRect().height}px`,
      );
    apply();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(apply);
      observer.observe(el);
    }
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      if (observer) observer.disconnect();
    };
  }, [tab, globalState, detailState]);

  if (detailRes.error?.status === 404) return <EmptyState title="RAPID not found" />;
  if (detailRes.error) return <ErrorState error={detailRes.error} onRetry={detailRes.refresh} />;
  if (detailRes.loading || !globalState || !detailState) return <LoadingSkeleton rows={10} />;
  if (!detail || !rapid) return <EmptyState title="RAPID not found" />;

  const roleLens = globalState.roleLens;
  const modifiedLabel =
    globalState.modified || detailState.modified ? 'Modified' : 'Baseline (unchanged)';

  const criteria = rapid.criteriaScore || {};
  const criteriaTotal = Number(criteria.total);
  const criteriaThreshold = Number(criteria.threshold) || 45;
  const hasCriteria = Number.isFinite(criteriaTotal);
  const criteriaTone = hasCriteria
    ? deriveKpiTone({ value: criteriaTotal, target: criteriaThreshold })
    : 'slate';
  // External UAT FINDING-009: every weighted contribution renders — a partial
  // slice left the visible sum (67.0) irreconcilable with the entry score (72.0).
  const criteriaRows = criteria.rows || [];

  const timeline = rapid.timeline || {};
  const elapsed = Number(timeline.daysElapsed) || 0;
  const remaining = Number(timeline.daysRemaining) || 0;
  const totalDays = Number(timeline.totalClockDays) || elapsed + remaining || 0;
  const totalWeeks = Number(timeline.totalWeeks) || (totalDays ? Math.round(totalDays / 7) : 6);
  const currentWeek = Number(timeline.currentWeek) || 0;
  const clockPct = totalDays ? Math.round((elapsed / totalDays) * 100) : 0;
  const clockTone = deriveKpiTone({
    value: remaining,
    target: totalDays ? totalDays * 0.25 : remaining,
  });
  const weekCells = Array.from({ length: totalWeeks || 0 }, (_, index) => {
    const weekNumber = index + 1;
    if (weekNumber < currentWeek) return 'is-elapsed';
    if (weekNumber === currentWeek) return 'is-current';
    return '';
  });

  const value = rapid.valueSummary || {};
  const closeoutReadiness = rapid.closeout?.closeoutReadiness || null;

  const heroItems = [
    hasCriteria
      ? {
          id: 'criteria-score',
          label: 'Criteria score',
          value: criteriaTotal.toFixed(1),
          unit: `/ ${criteriaThreshold}`,
          status: criteriaTone,
          statusLabel: criteriaTotal >= criteriaThreshold ? 'Qualifies' : 'Below threshold',
          footer: formatCriteriaRecommendation(criteria) || 'Weighted criteria matrix total',
          icon: '🎯',
        }
      : null,
    totalDays
      ? {
          id: 'clock',
          label: 'Six-week clock',
          value: `${remaining}`,
          unit: 'days left',
          status: clockTone,
          statusLabel: `Week ${currentWeek} of ${totalWeeks}`,
          footer: `${elapsed} elapsed / ${remaining} remaining / ${totalDays} total days`,
          icon: '⏱',
        }
      : null,
    value.displayValue
      ? {
          id: 'value',
          label: 'Cost avoidance',
          value: value.displayValue,
          status: 'blue',
          statusLabel: 'Estimated case',
          footer: value.costAvoidanceUsd
            ? `${formatMoney(value.costAvoidanceUsd)} · ${value.sustainmentConfidence || 'estimate'}`
            : value.sustainmentConfidence || 'Estimated value case',
          icon: '$',
        }
      : null,
    closeoutReadiness
      ? {
          id: 'closeout',
          label: 'Closeout readiness',
          value: `${closeoutReadiness.percent}`,
          unit: '%',
          status: deriveKpiTone({ value: closeoutReadiness.percent, target: 100 }),
          statusLabel: `${closeoutReadiness.complete}/${closeoutReadiness.total} items`,
          footer: 'Closeout checklist completion',
          icon: '✓',
        }
      : null,
  ].filter(Boolean);

  function updateRoleLens(nextRole) {
    const next = persistGlobalState(rapid.seedVersion, { ...globalState, roleLens: nextRole });
    setGlobalState(next);
  }

  function updateDetailState(nextState) {
    const persisted = persistDetailState(rapid, nextState);
    setDetailState(persisted);
    // Local storage is only a cache — the decision batch persists server-side
    // with the signed-in reviewer's attribution (AC-RAPID-4).
    scheduleReviewSync(rapid.id, persisted);
  }

  function updateTour(next) {
    setTourState(next);
    writeTourState(next);
  }

  function goToTourStep(step) {
    const totalSteps = detail.rapid.guidedTour?.steps?.length || 16;
    const bounded = Math.max(1, Math.min(step, totalSteps));
    const next = { open: true, step: bounded };
    updateTour(next);
    navigate(tourRouteForStep(bounded, rapid.id));
  }

  async function confirmReset() {
    try {
      // Drop any pending review-state sync so a stale batch cannot re-save
      // modified state on top of the restored baseline.
      cancelReviewSync();
      await resetMutation.run(() =>
        api.post('/admin/seed/reset', { preserveRapidDemoFeedback: true }),
      );
      clearRapidBrowserState();
      setGlobalState(persistGlobalState(rapid.seedVersion, {}));
      setTourState({ open: false, step: 1 });
      setGeneratedBrief(null);
      setShowResetDialog(false);
      await detailRes.refresh();
      toast.success('Workspace restored to baseline.');
    } catch (error) {
      toast.error(error.message || 'Reset failed');
    }
  }

  async function generateBrief() {
    try {
      const response = await briefMutation.run(() =>
        api.post(`/rapids/${rapid.id}/generate-brief`, {}),
      );
      setGeneratedBrief(response.data?.briefing || null);
      updateDetailState({
        ...detailState,
        createdBriefPreview: true,
        activityEntries: [
          {
            ts: nowTimestamp(),
            actor: 'Local operator',
            event: 'Governance brief preview generated.',
          },
          ...(detailState.activityEntries || []),
        ],
      });
      await detailRes.refresh();
    } catch (error) {
      toast.error(error.message || 'Could not generate brief preview');
    }
  }

  async function validateCloseout(status) {
    try {
      const response = await validateMutation.run(() =>
        api.post(`/rapids/${rapid.id}/validate`, { status }),
      );
      const followOnWorkItem = response.data?.followOnWorkItem || null;
      updateDetailState({
        ...detailState,
        closeout: {
          ...detailState.closeout,
          validationStatus: status,
        },
        createdActions: followOnWorkItem
          ? [...(detailState.createdActions || []), followOnWorkItem]
          : detailState.createdActions,
        activityEntries: [
          {
            ts: nowTimestamp(),
            actor: 'Local operator',
            event: `Sponsor validation recorded: ${status}.`,
          },
          ...(detailState.activityEntries || []),
        ],
      });
      await detailRes.refresh();
    } catch (error) {
      toast.error(error.message || 'Could not update closeout validation');
    }
  }

  const panelByTab = {
    overview: <OverviewTab detail={detail} detailState={detailState} />,
    intake: <IntakeTab rapid={rapid} detailState={detailState} onStateChange={updateDetailState} />,
    criteria: <CriteriaTab rapid={rapid} />,
    qa: <QaTab detail={detail} detailState={detailState} onStateChange={updateDetailState} />,
    approval: <ApprovalTab detail={detail} detailState={detailState} />,
    pods: <PodsTab detail={detail} />,
    diagnose: <DiagnosisTab detail={detail} onOpenEvidence={setEvidenceDrawer} />,
    canvas: (
      <CanvasTab detail={detail} detailState={detailState} onStateChange={updateDetailState} />
    ),
    actions: <ActionsTab detail={detail} detailState={detailState} />,
    metrics: <MetricsTab detail={detail} detailState={detailState} />,
    governance: (
      <GovernanceTab
        detail={detail}
        detailState={detailState}
        onGenerateBrief={generateBrief}
        generatingBrief={briefMutation.running}
        generatedBrief={generatedBrief}
        onOpenEvidence={setEvidenceDrawer}
      />
    ),
    closeout: (
      <CloseoutTab
        detail={detail}
        detailState={detailState}
        onStateChange={updateDetailState}
        onValidate={validateCloseout}
        validating={validateMutation.running}
        onOpenEvidence={setEvidenceDrawer}
      />
    ),
    shells: <ShellsTab rapid={rapid} />,
  };

  return (
    <div className="rapid-detail-page" data-testid="page-rapid-detail">
      <Breadcrumbs items={[{ to: '/rapids', label: 'RAPID' }, { label: rapid.title }]} />
      <SeededBanner banner={rapid.banner} />
      {operatorModeEnabled ? (
        <>
          <RapidStateStrip
            modifiedLabel={modifiedLabel}
            onReset={() => setShowResetDialog(true)}
            onStartTour={() => goToTourStep(1)}
            tourOpen={tourState.open}
            roleLens={roleLens}
            roleViews={rapid.roleViews}
            onRoleLensChange={updateRoleLens}
          />
          <GuidedTourPanel
            guidedTour={rapid.guidedTour}
            tourState={tourState}
            onBack={() => goToTourStep(Math.max(1, tourState.step - 1))}
            onNext={() => {
              const total = rapid.guidedTour?.steps?.length || 16;
              if (tourState.step >= total) {
                updateTour({ open: false, step: 1 });
                return;
              }
              goToTourStep(tourState.step + 1);
            }}
            onSkip={() => updateTour({ open: false, step: 1 })}
            onRestart={() => goToTourStep(1)}
          />
        </>
      ) : null}
      <RapidEvidenceDrawer
        evidence={evidenceDrawer}
        open={Boolean(evidenceDrawer)}
        onClose={() => setEvidenceDrawer(null)}
      />

      <section className="rapid-hero rapid-redesign-hero" data-testid="rapid-detail-workspace">
        <div className="rapid-redesign-hero__intro">
          <div className="eyebrow">{rapid.id}</div>
          <h1>{rapid.title}</h1>
          <p>{rapid.summary}</p>
          <div className="rapid-chip-row rapid-redesign-hero__chips">
            <HealthIndicator health={rapid.healthOverall} />
            <span className="chip outline sm">{rapid.visibleCategory}</span>
            <span className="chip outline sm">{rapid.loe}</span>
            <span className="chip outline sm">{rapid.timeline?.daysRemaining} days remaining</span>
          </div>
        </div>

        {heroItems.length ? (
          <KpiHeroBand eyebrow="RAPID scoreboard" items={heroItems} columns={heroItems.length} />
        ) : null}

        <div className="rapid-redesign-boardrow">
          {hasCriteria ? (
            <div className="rapid-redesign-gauge-cell">
              <GaugeCard
                value={Number(criteriaTotal.toFixed(1))}
                min={0}
                max={90}
                label="Criteria score"
                sublabel={`Threshold ${criteriaThreshold}`}
                tone={criteriaTone}
                deltaLabel={`${criteriaTotal - criteriaThreshold >= 0 ? '+' : ''}${(
                  criteriaTotal - criteriaThreshold
                ).toFixed(0)} vs threshold`}
                deltaTone={criteriaTone}
              />
            </div>
          ) : null}

          {totalDays ? (
            <div className="card rapid-redesign-clock">
              <div className="rapid-redesign-clock__head">
                <span className="eyebrow">Six-week clock</span>
                <span className="rapid-redesign-clock__value">{remaining} days left</span>
              </div>
              <div className="score-bar" aria-hidden>
                <i className={clockTone} style={{ width: `${clockPct}%` }} />
              </div>
              <div
                className="rapid-redesign-clock__weeks"
                role="img"
                aria-label={`Week ${currentWeek} of ${totalWeeks}; ${remaining} days remaining of ${totalDays}.`}
              >
                {weekCells.map((state, index) => (
                  <span
                    key={`week-${index + 1}`}
                    className={`rapid-redesign-clock__week ${state}`}
                  />
                ))}
              </div>
              <div className="rapid-redesign-clock__legend">
                <span>
                  <i className="elapsed" />
                  {elapsed} elapsed
                </span>
                <span>
                  <i className="current" />
                  Week {currentWeek} of {totalWeeks}
                </span>
                <span>
                  <i className="remaining" />
                  {remaining} remaining
                </span>
              </div>
            </div>
          ) : null}

          {criteriaRows.length ? (
            <div className="card rapid-redesign-criteria-strip" data-testid="rapid-criteria-strip">
              <div className="rapid-redesign-criteria-strip__head">
                <span className="eyebrow">Per-criterion contribution</span>
                <span className="chip outline sm">Weighted of {criteriaThreshold}</span>
              </div>
              {criteriaRows.map((row) => {
                const contribution = Number(row.weightedContribution) || 0;
                return (
                  <div className="rapid-redesign-criteria-row" key={row.key || row.criterion}>
                    <span className="rapid-redesign-criteria-row__label" title={row.criterion}>
                      {row.criterion}
                    </span>
                    <InlineScoreCell
                      value={Number(contribution.toFixed(1))}
                      max={20}
                      tone={bandTone(contribution, { good: 12, warn: 6 })}
                      ariaLabel={`${row.criterion}: weighted ${contribution.toFixed(1)}`}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>

      {rapid.kind === 'routed' ? (
        <RoutedCandidateView detail={detail} />
      ) : (
        <>
          {/* AC-RAPID-3: 6 lifecycle phases are the primary nav; the original
              13 tabs stay reachable nested inside their phase, so old ?tab=
              deep links keep working and land highlighted in the right phase. */}
          <section
            className="rapid-tabs rapid-phase-tabbar"
            data-testid="rapid-detail-tabs"
            ref={tabsRef}
          >
            {DETAIL_PHASES.map(([phaseKey, phaseLabel, tabKeys]) => {
              const phaseActive = tabKeys.includes(tab);
              return (
                <div
                  key={phaseKey}
                  className={`rapid-phase-group${phaseActive ? ' is-active' : ''}`}
                  data-testid={`rapid-phase-group-${phaseKey}`}
                >
                  <button
                    type="button"
                    className="rapid-phase-group__head"
                    data-testid={`rapid-phase-nav-${phaseKey}`}
                    aria-current={phaseActive ? 'true' : undefined}
                    onClick={() =>
                      setSearchParams(buildDetailSearch(searchParams, tabKeys[0]), {
                        replace: false,
                      })
                    }
                  >
                    {phaseLabel}
                  </button>
                  <div
                    className="rapid-phase-group__tabs"
                    role="tablist"
                    aria-label={`${phaseLabel} phase sections`}
                  >
                    {tabKeys.map((key) => (
                      <button
                        key={key}
                        role="tab"
                        aria-selected={tab === key}
                        className={`btn ${tab === key ? 'primary' : 'ghost'} sm`}
                        onClick={() =>
                          setSearchParams(buildDetailSearch(searchParams, key), { replace: false })
                        }
                        type="button"
                      >
                        {DETAIL_TAB_LABELS[key]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>

          {panelByTab[tab] || null}
        </>
      )}

      <ResetDialog
        open={operatorModeEnabled && showResetDialog}
        resetting={resetMutation.running}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={confirmReset}
      />
    </div>
  );
}
